import React from 'react';

interface Props {
    data: number[];
    width?: number;
    height?: number;
    className?: string;
}

/**
 * Tiny inline revenue trend line. Shape carries the trend; the stroke color
 * (green up / red down / gray flat) only reinforces it.
 */
const Sparkline: React.FC<Props> = ({ data, width = 120, height = 32, className }) => {
    if (!data.length || data.every((v) => v === 0)) {
        return (
            <svg width={width} height={height} className={className} aria-hidden="true">
                <line
                    x1={2}
                    y1={height / 2}
                    x2={width - 2}
                    y2={height / 2}
                    stroke="#d1d5db"
                    strokeWidth={2}
                    strokeLinecap="round"
                />
            </svg>
        );
    }

    const pad = 2;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;

    const points = data
        .map((v, i) => {
            const x = pad + (i * (width - 2 * pad)) / Math.max(data.length - 1, 1);
            const y = height - pad - ((v - min) / range) * (height - 2 * pad);
            return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(' ');

    // trend: mean of the second half vs the first half
    const mid = Math.floor(data.length / 2);
    const mean = (arr: number[]) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
    const delta = mean(data.slice(mid)) - mean(data.slice(0, mid));
    const stroke = delta > 0 ? '#16a34a' : delta < 0 ? '#dc2626' : '#9ca3af';

    return (
        <svg width={width} height={height} className={className} aria-hidden="true">
            <polyline
                points={points}
                fill="none"
                stroke={stroke}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
};

export default Sparkline;
